import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { PageWrap } from '../components/Main';
import { Box, Button, FormField, Input, Text } from '../components/ui';
import { useResetPasswordMutation } from '../graphql';
import { useRouter } from 'next/router';
import Register from '../components/layouts/Register';
import PasswordStrengthBar from '../components/PasswordStrengthBar';
import {
  validatePassword,
  parsePasswordError,
} from '../lib/password-validation';

const SetPassword = (): JSX.Element => {
  const router = useRouter();

  const email = router?.query.email;

  const [status, setStatus] = useState<JSX.Element | string | null>(null);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const currentToken: string | null =
    typeof router?.query.token === 'string' ? router?.query.token : null;
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordAgain, setNewPasswordAgain] = useState<string>('');
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const [
    resetPassword,
    { error: changePasswordError, data: changePasswordResult },
  ] = useResetPasswordMutation();

  const onSubmit = useCallback(async () => {
    if (typeof email !== 'string') {
      setStatus(
        'Email missing. Please make sure the web page address is exactly as in the email you received.',
      );
      return;
    }
    if (!newPassword) {
      setStatus('New password missing');
      return;
    }
    if (newPassword !== newPasswordAgain) {
      setStatus('New passwords do not match');
      return;
    }
    const validation = validatePassword(newPassword, email);
    if (!validation.valid) {
      setStatus(validation.error);
      return;
    }
    if (!currentToken) {
      setStatus(
        'Reset token missing. Please make sure the web page address is exactly as in the email you received.',
      );
      return;
    }
    if (!isAccepted) {
      setStatus(
        'You need to accept the terms and conditions in order to proceed.',
      );
      return;
    }
    setStatus('Changing');

    resetPassword({
      variables: {
        input: {
          email,
          loginToken: currentToken,
          newpassword: newPassword,
        },
      },
    });
  }, [
    email,
    newPassword,
    newPasswordAgain,
    currentToken,
    isAccepted,
    resetPassword,
  ]);

  useEffect(() => {
    if (changePasswordError) {
      const errorMessage = changePasswordError.message || '';
      const parsedError = parsePasswordError(errorMessage);
      if (parsedError) {
        setStatus(parsedError);
      } else {
        setStatus(
          <>
            Password change failed. Maybe the link was already used? If not,
            verify the web page address is exactly as in the email you received,
            or request a <Link href="/forgot-password">new password reset</Link>
            .
          </>,
        );
      }
    }
  }, [changePasswordError]);

  useEffect(() => {
    if (changePasswordResult) {
      setStatus(
        <>
          Password set. You can now login at{' '}
          <Link href="/signin">the signin page</Link>.
        </>,
      );
    }
  }, [changePasswordResult]);

  return (
    <PageWrap>
      <Register>
        <Box width="100%">
          <form>
            {!currentToken && (
              <FormField label="Current password">
                <Input
                  disabled={!!changePasswordResult}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  id="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  height="3rem"
                />
              </FormField>
            )}
            <FormField label="New password">
              <Input
                disabled={!!changePasswordResult}
                type="password"
                name="newpassword"
                autoComplete="new-password"
                id="newpassword"
                minLength={15}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                height="3rem"
              />
              <PasswordStrengthBar password={newPassword} />
            </FormField>
            <FormField label="New password again" mb={4}>
              <Input
                disabled={!!changePasswordResult}
                type="password"
                name="newpasswordagain"
                autoComplete="new-password"
                id="newpasswordagain"
                minLength={15}
                value={newPasswordAgain}
                onChange={(e) => setNewPasswordAgain(e.target.value)}
                height="3rem"
              />
            </FormField>
            <FormField>
              <Box as="label" display="flex" alignItems="center" mb={4}>
                <input
                  type="checkbox"
                  checked={isAccepted}
                  onChange={() => setIsAccepted(!isAccepted)}
                />
                &nbsp;&nbsp;I accept the &nbsp;
                <a
                  href="https://seaber.io/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </a>
              </Box>
            </FormField>
            <Button variant="primary" onPress={onSubmit} width="100%">
              {!currentToken ? 'Change password' : 'Set password'}
            </Button>
            {status && (
              <Box
                bg="neutral.white_100"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="neutral.gray_225"
                p={3}
                mt={4}
              >
                <Text>{status}</Text>
              </Box>
            )}
          </form>
        </Box>
      </Register>
    </PageWrap>
  );
};

SetPassword.publicPage = true;
export default SetPassword;
